import { apiFetch } from "./client";

export type UserLifecycleAction =
  | "deactivate"
  | "issue-warning"
  | "suspend"
  | "terminate";

export type UserLifecyclePayload = {
  readonly reason: string;
  readonly policy_section?: string;
  readonly incident_summary?: string;
  readonly suspension_end?: string;
  readonly appeal_deadline?: string;
  readonly data_export_url?: string;
};

export async function runUserLifecycleAction(
  userId: string,
  action: UserLifecycleAction,
  payload: UserLifecyclePayload,
): Promise<void> {
  if (action === "deactivate") {
    await deactivateUser(userId, payload);
    return;
  }
  if (action === "issue-warning") {
    await issueUserWarning(userId, payload);
    return;
  }
  if (action === "suspend") {
    await suspendUser(userId, payload);
    return;
  }
  await terminateUser(userId, payload);
}

export async function deactivateUser(
  userId: string,
  payload: Pick<UserLifecyclePayload, "reason">,
): Promise<void> {
  await apiFetch<{ data: unknown }>(`/admin/users/${userId}/deactivate`, {
    method: "POST",
    body: JSON.stringify(compactPayload(payload)),
  });
}

export async function issueUserWarning(
  userId: string,
  payload: Pick<
    UserLifecyclePayload,
    "reason" | "policy_section" | "incident_summary"
  >,
): Promise<void> {
  await apiFetch<{ data: unknown }>(`/admin/users/${userId}/issue-warning`, {
    method: "POST",
    body: JSON.stringify(compactPayload(payload)),
  });
}

export async function suspendUser(
  userId: string,
  payload: Pick<UserLifecyclePayload, "reason" | "suspension_end">,
): Promise<void> {
  await apiFetch<{ data: unknown }>(`/admin/users/${userId}/suspend`, {
    method: "POST",
    body: JSON.stringify(compactPayload(payload)),
  });
}

export async function terminateUser(
  userId: string,
  payload: Pick<
    UserLifecyclePayload,
    "reason" | "appeal_deadline" | "data_export_url"
  >,
): Promise<void> {
  await apiFetch<{ data: unknown }>(`/admin/users/${userId}/terminate`, {
    method: "POST",
    body: JSON.stringify(compactPayload(payload)),
  });
}

function compactPayload(payload: UserLifecyclePayload): Record<string, string> {
  const entries = Object.entries(payload).flatMap(([key, value]) => {
    if (typeof value !== "string") return [];
    const trimmed = value.trim();
    return trimmed.length > 0 ? [[key, trimmed] as const] : [];
  });
  return Object.fromEntries(entries);
}
